import UserService from '../../services/user.service'
import SearchService from '../../services/search.service'
import { extractList, getUserIdentityValues } from '../../utils/members'

export function findParticipantById(items: any[], userId: string) {
    return items.find((item) => getUserIdentityValues(item).includes(`id:${userId}`))
}

export async function loadParticipantById(
    userId: string,
    email?: string | null,
    query?: string | null,
    getUsers = UserService.getUsers,
    searchUsers = (search: string) => SearchService.search({
        query: search,
        types: ['user'],
        limit: 50,
    }),
) {
    const searches = Array.from(new Set([email, query, userId]
        .map((value) => value?.trim())
        .filter((value): value is string => Boolean(value))))

    // An exact email (or the original query) usually resolves the link in one request.
    const preferredSearch = searches[0]
    try {
        const data = await getUsers({ search: preferredSearch, limit: 1000 })
        const match = findParticipantById(extractList(data), userId)
        if (match) return match
    } catch {
        // The global search API may still have this user.
    }

    // Try global search next: its result may be absent from the user list.
    for (const search of Array.from(new Set([query, email, userId]
        .map((value) => value?.trim())
        .filter((value): value is string => Boolean(value))))) {
        try {
            const response = await searchUsers(search)
            const result = response.results.find((item) => String(item.id) === userId)
            if (result) {
                return {
                    id: result.id,
                    name: result.title,
                    email: result.subtitle || email || '',
                    avatar_url: result.avatarUrl,
                }
            }
        } catch {
            // Continue with the remaining lookup hints.
        }
    }

    for (const search of searches.slice(1)) {
        try {
            const data = await getUsers({ search, limit: 1000 })
            const match = findParticipantById(extractList(data), userId)
            if (match) return match
        } catch {
            // Continue with the paginated user list.
        }
    }

    // Older links may have no search hint. The user list is paginated, so
    // keep looking until the backend runs out of records.
    const pageSize = 1000
    const seenFirstIds = new Set<string>()
    for (let skip = 0; ; skip += pageSize) {
        const data = await getUsers({ skip, limit: pageSize })
        const items = extractList(data)
        const match = findParticipantById(items, userId)
        if (match) return match
        if (items.length < pageSize) return null

        const firstIds = getUserIdentityValues(items[0]).join('|')
        if (seenFirstIds.has(firstIds)) return null
        seenFirstIds.add(firstIds)
    }
}
